import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2, Play, Square, Plus, Clock, DollarSign, Briefcase, ChevronLeft, ChevronRight, Download, Trash2, Edit, X } from 'lucide-react';
import { time as timeApi, matters as mattersApi } from '@/lib/api';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const formatMinutes = (m) => {
  if (!m) return '0h';
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return h ? `${h}h ${mm}m` : `${mm}m`;
};
const formatMoney = (cents) => `A$${((cents || 0) / 100).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const startOfIsoWeek = (d) => {
  const dt = new Date(d);
  const day = dt.getUTCDay(); // 0=Sun, 1=Mon...
  const diff = (day === 0 ? -6 : 1 - day);
  dt.setUTCDate(dt.getUTCDate() + diff);
  dt.setUTCHours(0, 0, 0, 0);
  return dt;
};

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export default function TimeTrackingPage({ matterId = null, matterRef = null, embedded = false }) {
  const [running, setRunning] = React.useState(null);
  const [tick, setTick] = React.useState(0);
  const [entries, setEntries] = React.useState([]);
  const [totals, setTotals] = React.useState({});
  const [wip, setWip] = React.useState(null);
  const [matters, setMatters] = React.useState([]);
  const [weekStart, setWeekStart] = React.useState(startOfIsoWeek(new Date()));
  const [sheet, setSheet] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [startOpen, setStartOpen] = React.useState(false);
  const [manualOpen, setManualOpen] = React.useState(false);
  const [editing, setEditing] = React.useState(null);
  const [xeroOpen, setXeroOpen] = React.useState(false);
  const [xeroMatter, setXeroMatter] = React.useState(null);
  const [selectedIds, setSelectedIds] = React.useState({});

  // Live timer tick every second
  React.useEffect(() => {
    if (!running) return;
    const iv = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(iv);
  }, [running]);

  const loadAll = React.useCallback(() => {
    setLoading(true);
    const listOpts = matterId ? { matter_id: matterId } : {};
    Promise.all([
      timeApi.running(),
      timeApi.list(listOpts),
      timeApi.wip(),
      timeApi.timesheet({ week_start: weekStart.toISOString() }),
      embedded ? Promise.resolve([]) : mattersApi.list(),
    ]).then(([run, ls, w, s, ms]) => {
      setRunning(run);
      setEntries(ls.items || []);
      setTotals(ls.totals || {});
      setWip(w);
      setSheet(s);
      setMatters(ms.items || ms || []);
    }).catch((e) => {
      toast.error('Failed to load time data');
      console.error(e);
    }).finally(() => setLoading(false));
  }, [weekStart, matterId, embedded]);

  React.useEffect(loadAll, [loadAll]);

  const runningElapsed = () => {
    if (!running?.started_at) return '0:00';
    const start = new Date(running.started_at).getTime();
    const sec = Math.max(0, Math.floor((Date.now() - start) / 1000));
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = sec % 60;
    return h ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}` : `${m}:${String(s).padStart(2, '0')}`;
  };
  void tick;

  const stopTimer = async () => {
    if (!running) return;
    try {
      await timeApi.stop(running.id);
      toast.success('Timer stopped');
      loadAll();
    } catch (e) {
      toast.error('Failed to stop timer');
    }
  };

  const doDelete = async (id) => {
    if (!window.confirm('Delete this time entry? This cannot be undone.')) return;
    try {
      await timeApi.remove(id);
      toast.success('Entry deleted');
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const openXeroExport = (m) => {
    setXeroMatter(m);
    setSelectedIds({});
    setXeroOpen(true);
  };

  return (
    <div className={embedded ? '' : 'p-8'} data-testid="time-tracking-page">
      {!embedded && (
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-serif text-3xl font-bold text-[#1A3A5C]">Time tracking</h1>
            <p className="mt-1 text-sm text-slate-500">Billable hours, live timers, weekly timesheet & WIP.</p>
          </div>
          <div className="flex items-center gap-2">
            {running ? (
              <div
                data-testid="time-running-badge"
                className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800"
              >
                <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-500" />
                <span className="font-semibold">{runningElapsed()}</span>
                <span className="text-emerald-700">{running.description}</span>
                <Button size="sm" variant="ghost" onClick={stopTimer} data-testid="time-stop-running">
                  <Square className="h-4 w-4" /> Stop
                </Button>
              </div>
            ) : null}
            <Button onClick={() => setStartOpen(true)} data-testid="time-start-btn">
              <Play className="mr-2 h-4 w-4" /> Start timer
            </Button>
            <Button variant="outline" onClick={() => setManualOpen(true)} data-testid="time-log-btn">
              <Plus className="mr-2 h-4 w-4" /> Log time
            </Button>
          </div>
        </div>
      )}

      {embedded && (
        <div className="mb-4 flex flex-wrap items-center gap-3">
          {running && running.matter_id === matterId ? (
            <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800" data-testid="time-embedded-running">
              <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-500" />
              <span className="font-semibold">{runningElapsed()}</span>
              <span>{running.description}</span>
              <Button size="sm" variant="ghost" onClick={stopTimer} data-testid="time-embedded-stop">
                <Square className="h-4 w-4" /> Stop
              </Button>
            </div>
          ) : (
            <Button onClick={() => setStartOpen(true)} data-testid="time-embedded-start">
              <Play className="mr-2 h-4 w-4" /> Start timer on this matter
            </Button>
          )}
          <Button variant="outline" onClick={() => setManualOpen(true)} data-testid="time-embedded-log">
            <Plus className="mr-2 h-4 w-4" /> Log time
          </Button>
        </div>
      )}

      {/* KPI row */}
      <div className={`grid gap-4 ${embedded ? 'md:grid-cols-3' : 'md:grid-cols-4'}`}>
        <KpiCard icon={Clock} label="Total tracked" value={formatMinutes(totals.total_minutes)} sub={`${formatMinutes(totals.billable_minutes)} billable`} testid="time-kpi-total" />
        <KpiCard icon={DollarSign} label="Total value" value={formatMoney(totals.total_amount_cents)} sub={`${formatMoney(totals.unbilled_amount_cents)} unbilled`} testid="time-kpi-value" />
        <KpiCard icon={Briefcase} label="Unbilled WIP" value={formatMoney(wip?.total_amount_cents)} sub={`${wip?.matter_count || 0} matters`} testid="time-kpi-wip" />
        {!embedded && <KpiCard icon={Clock} label="Entries" value={entries.length} sub={running ? 'Timer running' : 'No live timer'} testid="time-kpi-entries" />}
      </div>

      {loading ? (
        <div className="mt-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#2ECC9A]" /></div>
      ) : (
        <Tabs defaultValue={embedded ? 'entries' : 'timesheet'} className="mt-6">
          <TabsList data-testid="time-tabs">
            {!embedded && <TabsTrigger value="timesheet" data-testid="time-tab-timesheet">Weekly timesheet</TabsTrigger>}
            <TabsTrigger value="entries" data-testid="time-tab-entries">Entries</TabsTrigger>
            {!embedded && <TabsTrigger value="wip" data-testid="time-tab-wip">WIP by matter</TabsTrigger>}
          </TabsList>

          {!embedded && (
            <TabsContent value="timesheet" className="mt-4">
              <TimesheetGrid sheet={sheet} weekStart={weekStart} setWeekStart={setWeekStart} />
            </TabsContent>
          )}

          <TabsContent value="entries" className="mt-4">
            <EntriesList entries={entries} onDelete={doDelete} onEdit={setEditing} embedded={embedded} />
          </TabsContent>

          {!embedded && (
            <TabsContent value="wip" className="mt-4">
              <WipList wip={wip} onExport={openXeroExport} />
            </TabsContent>
          )}
        </Tabs>
      )}

      <StartTimerDialog
        open={startOpen}
        onClose={() => setStartOpen(false)}
        matters={matters}
        defaultMatterId={matterId}
        onSaved={loadAll}
      />
      <ManualEntryDialog
        open={manualOpen}
        onClose={() => setManualOpen(false)}
        matters={matters}
        defaultMatterId={matterId}
        onSaved={loadAll}
      />
      <EditEntryDialog
        entry={editing}
        onClose={() => setEditing(null)}
        matters={matters}
        onSaved={loadAll}
      />
      <XeroExportDialog
        open={xeroOpen}
        onClose={() => setXeroOpen(false)}
        matter={xeroMatter}
        selectedIds={selectedIds}
        setSelectedIds={setSelectedIds}
        onDone={loadAll}
      />
    </div>
  );
}

const KpiCard = ({ icon: Icon, label, value, sub, testid }) => (
  <Card data-testid={testid}>
    <CardContent className="p-5">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500"><Icon className="h-4 w-4" />{label}</div>
      <div className="mt-2 font-serif text-2xl font-bold text-[#1A3A5C]">{value}</div>
      {sub && <div className="text-xs text-slate-500">{sub}</div>}
    </CardContent>
  </Card>
);

function TimesheetGrid({ sheet, weekStart, setWeekStart }) {
  const shift = (delta) => {
    const d = new Date(weekStart);
    d.setUTCDate(d.getUTCDate() + delta * 7);
    setWeekStart(d);
  };
  if (!sheet) return null;
  const dayKeys = Object.keys(sheet.days).sort();
  return (
    <Card>
      <CardContent className="p-5">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500">Week of</div>
            <div className="font-serif text-lg font-semibold text-[#1A3A5C]" data-testid="time-week-label">
              {new Date(sheet.week_start).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => shift(-1)} data-testid="time-week-prev"><ChevronLeft className="h-4 w-4" /></Button>
            <Button size="sm" variant="outline" onClick={() => setWeekStart(startOfIsoWeek(new Date()))} data-testid="time-week-today">This week</Button>
            <Button size="sm" variant="outline" onClick={() => shift(1)} data-testid="time-week-next"><ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
        <div className="grid grid-cols-7 gap-2">
          {dayKeys.map((k, i) => {
            const d = sheet.days[k];
            const isToday = k === new Date().toISOString().slice(0, 10);
            return (
              <div key={k} className={`rounded-lg border p-3 ${isToday ? 'border-[#2ECC9A] bg-emerald-50/40' : 'border-slate-200 bg-white'}`} data-testid={`time-day-${k}`}>
                <div className="text-xs font-semibold text-slate-600">{DAYS[i]}</div>
                <div className="text-xs text-slate-400">{new Date(k + 'T00:00:00Z').getUTCDate()}</div>
                <div className="mt-2 font-serif text-lg font-bold text-[#1A3A5C]">{formatMinutes(d.minutes)}</div>
                <div className="text-[11px] text-slate-500">{formatMoney(d.amount_cents)}</div>
                <div className="mt-1 text-[10px] uppercase tracking-wider text-emerald-700">{d.entries.length} {d.entries.length === 1 ? 'entry' : 'entries'}</div>
              </div>
            );
          })}
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-4 border-t border-slate-100 pt-4 text-sm">
          <div><span className="text-slate-500">Total: </span><span className="font-semibold text-[#1A3A5C]">{formatMinutes(sheet.total_minutes)}</span></div>
          <div><span className="text-slate-500">Billable: </span><span className="font-semibold text-emerald-700">{formatMinutes(sheet.total_billable_minutes)}</span></div>
          <div><span className="text-slate-500">Value: </span><span className="font-semibold text-[#1A3A5C]">{formatMoney(sheet.total_amount_cents)}</span></div>
        </div>
      </CardContent>
    </Card>
  );
}

function EntriesList({ entries, onDelete, onEdit, embedded }) {
  if (!entries.length) {
    return <Card><CardContent className="p-8 text-center text-sm text-slate-500">No time entries yet. Start a timer or log time to begin.</CardContent></Card>;
  }
  return (
    <Card>
      <CardContent className="p-0">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-100 text-left text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-4 py-3">When</th>
              <th className="px-4 py-3">Description</th>
              {!embedded && <th className="px-4 py-3">Matter</th>}
              <th className="px-4 py-3">Duration</th>
              <th className="px-4 py-3">Rate</th>
              <th className="px-4 py-3">Value</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {entries.map((e) => (
              <tr key={e.id} className="border-b border-slate-50" data-testid={`time-entry-${e.id}`}>
                <td className="px-4 py-3 text-xs text-slate-500">{new Date(e.started_at).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</td>
                <td className="px-4 py-3 font-medium text-[#1A3A5C]">{e.description}</td>
                {!embedded && (
                  <td className="px-4 py-3 text-xs">
                    {e.matter_ref ? <Link to={`/app/matters/${e.matter_id}`} className="text-[#2ECC9A] hover:underline">{e.matter_ref}</Link> : <span className="text-slate-400">—</span>}
                  </td>
                )}
                <td className="px-4 py-3 font-semibold text-[#1A3A5C]">{formatMinutes(e.minutes)}</td>
                <td className="px-4 py-3 text-xs text-slate-500">A${(e.rate_cents / 100).toFixed(0)}/hr</td>
                <td className="px-4 py-3 font-semibold text-emerald-700">{e.billable ? formatMoney(e.amount_cents) : <span className="text-slate-400">Non-billable</span>}</td>
                <td className="px-4 py-3">
                  {e.invoiced ? (
                    <Badge className="border border-blue-200 bg-blue-50 text-blue-700">Invoiced</Badge>
                  ) : e.ended_at ? (
                    <Badge className="border border-amber-200 bg-amber-50 text-amber-700">Unbilled</Badge>
                  ) : (
                    <Badge className="border border-emerald-200 bg-emerald-50 text-emerald-700">Running</Badge>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    {!e.invoiced && (
                      <>
                        <Button size="icon" variant="ghost" onClick={() => onEdit(e)} data-testid={`time-edit-${e.id}`}><Edit className="h-4 w-4" /></Button>
                        <Button size="icon" variant="ghost" onClick={() => onDelete(e.id)} data-testid={`time-delete-${e.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function WipList({ wip, onExport }) {
  if (!wip || !wip.matters?.length) {
    return <Card><CardContent className="p-8 text-center text-sm text-slate-500">Nothing in WIP — every billable hour has been invoiced.</CardContent></Card>;
  }
  return (
    <Card>
      <CardContent className="p-5">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500">Total unbilled</div>
            <div className="font-serif text-2xl font-bold text-emerald-700" data-testid="time-wip-total">{formatMoney(wip.total_amount_cents)}</div>
          </div>
          <div className="text-sm text-slate-500">{formatMinutes(wip.total_minutes)} across {wip.matter_count} matter{wip.matter_count === 1 ? '' : 's'}</div>
        </div>
        <table className="w-full text-sm">
          <thead className="border-b border-slate-100 text-left text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-4 py-3">Matter</th>
              <th className="px-4 py-3">Entries</th>
              <th className="px-4 py-3">Time</th>
              <th className="px-4 py-3">Value</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {wip.matters.map((m) => (
              <tr key={m.matter_id || 'no-matter'} className="border-b border-slate-50" data-testid={`time-wip-${m.matter_id || 'nomatter'}`}>
                <td className="px-4 py-3">
                  {m.matter_ref ? <Link to={`/app/matters/${m.matter_id}`} className="font-semibold text-[#2ECC9A] hover:underline">{m.matter_ref}</Link> : <span className="text-slate-400">Unassigned</span>}
                </td>
                <td className="px-4 py-3 text-slate-600">{m.entries}</td>
                <td className="px-4 py-3 font-semibold text-[#1A3A5C]">{formatMinutes(m.minutes)}</td>
                <td className="px-4 py-3 font-semibold text-emerald-700">{formatMoney(m.amount_cents)}</td>
                <td className="px-4 py-3 text-right">
                  {m.matter_id && (
                    <Button size="sm" variant="outline" onClick={() => onExport(m)} data-testid={`time-wip-export-${m.matter_id}`}>
                      <Download className="mr-2 h-4 w-4" /> Send to Xero
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function StartTimerDialog({ open, onClose, matters, defaultMatterId, onSaved }) {
  const [description, setDescription] = React.useState('');
  const [matterId, setMatterId] = React.useState(defaultMatterId || 'none');
  const [billable, setBillable] = React.useState(true);
  const [rate, setRate] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setDescription('');
      setMatterId(defaultMatterId || 'none');
      setBillable(true);
      setRate('');
    }
  }, [open, defaultMatterId]);

  const submit = async () => {
    if (!description.trim()) { toast.error('Description is required'); return; }
    setBusy(true);
    try {
      await timeApi.start({
        description: description.trim(),
        matter_id: matterId === 'none' ? null : matterId,
        billable,
        rate_cents: rate ? Math.round(parseFloat(rate) * 100) : null,
      });
      toast.success('Timer started');
      onSaved(); onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to start timer');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="time-start-dialog">
        <DialogHeader><DialogTitle>Start timer</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Description</label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Drafting engagement letter" data-testid="time-start-description" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Matter (optional)</label>
            <Select value={matterId} onValueChange={setMatterId}>
              <SelectTrigger data-testid="time-start-matter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— None —</SelectItem>
                {matters.map((m) => <SelectItem key={m.id} value={m.id}>{m.ref} · {m.applicant?.name || m.applicant_email}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={billable} onCheckedChange={setBillable} data-testid="time-start-billable" /> Billable
            </label>
            <div className="flex-1">
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Rate (A$/hr, optional)</label>
              <Input type="number" value={rate} onChange={(e) => setRate(e.target.value)} placeholder="250" data-testid="time-start-rate" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="time-start-submit">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Start
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ManualEntryDialog({ open, onClose, matters, defaultMatterId, onSaved }) {
  const [description, setDescription] = React.useState('');
  const [matterId, setMatterId] = React.useState(defaultMatterId || 'none');
  const [minutes, setMinutes] = React.useState('30');
  const [billable, setBillable] = React.useState(true);
  const [rate, setRate] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setDescription(''); setMatterId(defaultMatterId || 'none'); setMinutes('30'); setBillable(true); setRate('');
    }
  }, [open, defaultMatterId]);

  const submit = async () => {
    if (!description.trim()) { toast.error('Description is required'); return; }
    const m = parseInt(minutes, 10);
    if (!m || m <= 0) { toast.error('Minutes must be a positive number'); return; }
    setBusy(true);
    try {
      await timeApi.addManual({
        description: description.trim(),
        matter_id: matterId === 'none' ? null : matterId,
        minutes: m,
        billable,
        rate_cents: rate ? Math.round(parseFloat(rate) * 100) : null,
      });
      toast.success('Time entry logged');
      onSaved(); onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to log time');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="time-manual-dialog">
        <DialogHeader><DialogTitle>Log time</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Description</label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Phone call with client re: bridging visa" rows={3} data-testid="time-manual-description" />
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Minutes</label>
              <Input type="number" value={minutes} onChange={(e) => setMinutes(e.target.value)} data-testid="time-manual-minutes" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Rate (A$/hr, optional)</label>
              <Input type="number" value={rate} onChange={(e) => setRate(e.target.value)} placeholder="250" data-testid="time-manual-rate" />
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Matter (optional)</label>
            <Select value={matterId} onValueChange={setMatterId}>
              <SelectTrigger data-testid="time-manual-matter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— None —</SelectItem>
                {matters.map((m) => <SelectItem key={m.id} value={m.id}>{m.ref} · {m.applicant?.name || m.applicant_email}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={billable} onCheckedChange={setBillable} data-testid="time-manual-billable" /> Billable
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="time-manual-submit">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Log
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditEntryDialog({ entry, onClose, matters, onSaved }) {
  const [description, setDescription] = React.useState('');
  const [minutes, setMinutes] = React.useState('');
  const [billable, setBillable] = React.useState(true);
  const [rate, setRate] = React.useState('');
  const [matterId, setMatterId] = React.useState('none');
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (entry) {
      setDescription(entry.description);
      setMinutes(String(entry.minutes || 0));
      setBillable(!!entry.billable);
      setRate(String((entry.rate_cents || 0) / 100));
      setMatterId(entry.matter_id || 'none');
    }
  }, [entry]);

  if (!entry) return null;

  const submit = async () => {
    setBusy(true);
    try {
      const m = parseInt(minutes, 10);
      await timeApi.update(entry.id, {
        description,
        minutes: m,
        billable,
        rate_cents: Math.round(parseFloat(rate || '0') * 100),
        matter_id: matterId === 'none' ? null : matterId,
      });
      toast.success('Entry updated');
      onSaved(); onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!entry} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="time-edit-dialog">
        <DialogHeader><DialogTitle>Edit entry</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Description</label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} data-testid="time-edit-description" />
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Minutes</label>
              <Input type="number" value={minutes} onChange={(e) => setMinutes(e.target.value)} data-testid="time-edit-minutes" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Rate (A$/hr)</label>
              <Input type="number" value={rate} onChange={(e) => setRate(e.target.value)} data-testid="time-edit-rate" />
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Matter</label>
            <Select value={matterId} onValueChange={setMatterId}>
              <SelectTrigger data-testid="time-edit-matter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— None —</SelectItem>
                {matters.map((m) => <SelectItem key={m.id} value={m.id}>{m.ref} · {m.applicant?.name || m.applicant_email}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={billable} onCheckedChange={setBillable} data-testid="time-edit-billable" /> Billable
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="time-edit-submit">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function XeroExportDialog({ open, onClose, matter, selectedIds, setSelectedIds, onDone }) {
  const [entries, setEntries] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (!open || !matter?.matter_id) return;
    setLoading(true);
    timeApi.list({ matter_id: matter.matter_id, unbilled_only: true })
      .then((d) => {
        const ended = (d.items || []).filter((e) => e.billable && !e.invoiced && e.ended_at);
        setEntries(ended);
        setSelectedIds(Object.fromEntries(ended.map((e) => [e.id, true])));
      })
      .finally(() => setLoading(false));
  }, [open, matter, setSelectedIds]);

  const totalSelected = entries.filter((e) => selectedIds[e.id]).reduce((s, e) => s + (e.amount_cents || 0), 0);
  const minutesSelected = entries.filter((e) => selectedIds[e.id]).reduce((s, e) => s + (e.minutes || 0), 0);
  const selectedCount = entries.filter((e) => selectedIds[e.id]).length;

  const submit = async () => {
    const ids = entries.filter((e) => selectedIds[e.id]).map((e) => e.id);
    if (!ids.length) { toast.error('Select at least one entry'); return; }
    setBusy(true);
    try {
      const r = await timeApi.xeroExport({ entry_ids: ids, matter_id: matter.matter_id });
      toast.success(`Draft invoice created (${r.entries_marked_invoiced} entries)`);
      onDone(); onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Export failed');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="time-xero-dialog">
        <DialogHeader><DialogTitle>Send to Xero — {matter?.matter_ref}</DialogTitle></DialogHeader>
        {loading ? (
          <div className="flex justify-center py-6"><Loader2 className="h-6 w-6 animate-spin text-[#2ECC9A]" /></div>
        ) : (
          <div className="space-y-3">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
              <div className="flex items-center justify-between">
                <div><span className="text-slate-500">Selected: </span><span className="font-semibold">{selectedCount}</span> entries · <span className="font-semibold">{formatMinutes(minutesSelected)}</span></div>
                <div className="font-serif text-lg font-bold text-emerald-700">{formatMoney(totalSelected)}</div>
              </div>
            </div>
            <div className="max-h-72 overflow-y-auto rounded-lg border border-slate-200">
              {entries.length === 0 && <div className="p-4 text-center text-sm text-slate-500">No eligible entries for this matter</div>}
              {entries.map((e) => (
                <label key={e.id} className="flex cursor-pointer items-center justify-between border-b border-slate-100 px-3 py-2 text-sm hover:bg-slate-50" data-testid={`time-xero-row-${e.id}`}>
                  <div className="flex items-center gap-3">
                    <Checkbox checked={!!selectedIds[e.id]} onCheckedChange={(v) => setSelectedIds({ ...selectedIds, [e.id]: !!v })} />
                    <div>
                      <div className="font-medium text-[#1A3A5C]">{e.description}</div>
                      <div className="text-xs text-slate-500">{new Date(e.started_at).toLocaleDateString('en-AU')} · {formatMinutes(e.minutes)} · A${(e.rate_cents / 100).toFixed(0)}/hr</div>
                    </div>
                  </div>
                  <div className="font-semibold text-emerald-700">{formatMoney(e.amount_cents)}</div>
                </label>
              ))}
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || selectedCount === 0} data-testid="time-xero-submit">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Create draft invoice
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
